import { Select, Text } from "@mantine/core";
import { DataTableColumn } from "mantine-datatable";
import { useCallback, useMemo } from "react";

import { DatabaseMetadataPopover } from "../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import { TableData, TableRow } from "../../../components/Table/types.ts";
import {
  UpdateUserInput,
  UserRole,
  UserStatus,
} from "../../../graphql/generated";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import { User } from "../../../types/user.ts";
import { toLocalDateTime } from "../../../utils/date.ts";
import { toHumanReadable } from "../../../utils/string.ts";
import { getUserRoles, userStatuses } from "../constants.ts";
import { useStyles } from "../UserAccessSetting.styles.ts";

interface Props {
  data: User[] | null | undefined;
  canEdit?: boolean;
  onChange: (id: number, value: UpdateUserInput) => void;
}

export function useUsersTableData({
  data,
  canEdit = false,
  onChange,
}: Props): TableData {
  const { classes } = useStyles();

  const { user: currentUser } = useCurrentUserContext();

  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "email",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{ tableName: "User", fieldName: "email" }}
        >
          Email
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 240,
    },
    {
      accessor: "role",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{ tableName: "User", fieldName: "role" }}
        >
          Role
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 200,
    },
    {
      accessor: "status",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{ tableName: "User", fieldName: "status" }}
        >
          Status
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 120,
    },
    {
      accessor: "lastAuthenticated",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "User",
            fieldName: "lastAuthenticated",
          }}
        >
          Last Authenticated
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 200,
    },
    {
      accessor: "createdAt",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "User",
            fieldName: "createdAt",
          }}
        >
          Created Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
  ];

  const handleOnChange = useCallback(
    (id: number, value: UpdateUserInput) => {
      onChange(id, value);
    },
    [onChange]
  );

  const rows = useMemo(() => {
    return data?.map((user) => {
      return {
        email: <Text className={classes.emailWrapper}>{user.email}</Text>,
        role:
          canEdit && user.id !== currentUser?.id ? (
            <Select
              key={user.id}
              data={getUserRoles(currentUser)}
              value={user.role}
              placeholder="Select a role"
              size="s"
              withinPortal
              onChange={(value: UserRole) =>
                handleOnChange(user.id, { role: value, status: user.status })
              }
            />
          ) : (
            toHumanReadable(user.role)
          ),
        status:
          canEdit && user.id !== currentUser?.id ? (
            <Select
              key={user.id}
              data={userStatuses}
              value={user.status}
              placeholder="Select a status"
              size="s"
              onChange={(value: UserStatus) =>
                handleOnChange(user.id, { status: value, role: user.role })
              }
            />
          ) : (
            toHumanReadable(user.status)
          ),
        lastAuthenticated:
          user.lastAuthenticated && toLocalDateTime(user.lastAuthenticated),
        createdAt: toLocalDateTime(user.createdAt),
      };
    });
  }, [data, classes.emailWrapper, canEdit, currentUser, handleOnChange]);
  return [columns, rows];
}
